import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  FileText, Plus, Trash2, Edit2, Save, X, Play, Download,
  ChevronDown, ChevronUp, DollarSign, Eye, EyeOff, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMN_OPTIONS = [
  { field: 'name', label: 'Name' },
  { field: 'discipline', label: 'Discipline' },
  { field: 'state', label: 'State' },
  { field: 'weekly_count', label: 'Weekly Count' },
  { field: 'ideal_visits', label: 'Ideal' },
  { field: 'total', label: 'Total' },
  { field: 'raw_count', label: 'Raw Count' },
  { field: 'compensation', label: 'Compensation ($)' },
];

export default function ReportBuilder() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const [form, setForm] = useState({
    name: '', description: '', report_type: 'weekly',
    group_by: 'therapist', sub_group_by: 'state', date_range: 'last_6_weeks',
    columns: [
      { field: 'name', label: 'Therapist', visible: true },
      { field: 'discipline', label: 'Disc', visible: true },
      { field: 'total', label: 'Total', visible: true },
    ],
    weight_overrides: [],
    show_compensation: false, show_subtotals: true, show_grand_total: true,
    include_excluded_tasks: false,
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/report-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const resetForm = () => {
    setForm({
      name: '', description: '', report_type: 'weekly',
      group_by: 'therapist', sub_group_by: 'state', date_range: 'last_6_weeks',
      columns: [
        { field: 'name', label: 'Therapist', visible: true },
        { field: 'discipline', label: 'Disc', visible: true },
        { field: 'total', label: 'Total', visible: true },
      ],
      weight_overrides: [], show_compensation: false, show_subtotals: true,
      show_grand_total: true, include_excluded_tasks: false,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (t) => {
    setForm({
      name: t.name, description: t.description || '',
      report_type: t.report_type || 'weekly',
      group_by: t.group_by || 'therapist',
      sub_group_by: t.sub_group_by || 'state',
      date_range: t.date_range || 'last_6_weeks',
      columns: t.columns || [],
      weight_overrides: t.weight_overrides || [],
      show_compensation: t.show_compensation || false,
      show_subtotals: t.show_subtotals !== false,
      show_grand_total: t.show_grand_total !== false,
      include_excluded_tasks: t.include_excluded_tasks || false,
    });
    setEditingId(t.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingId) {
        await axios.put(`${API}/report-templates/${editingId}`, form, { headers });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/report-templates`, form, { headers });
        toast.success('Template created');
      }
      resetForm();
      fetchTemplates();
    } catch { toast.error('Failed to save template'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/report-templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  const handleSeedDefaults = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/report-templates/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchTemplates();
    } catch { toast.error('Failed to seed defaults'); }
  };

  const handleGenerate = async (templateId) => {
    setGeneratingId(templateId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/report-templates/${templateId}/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedReport(res.data);
      setExpandedId(templateId);
      toast.success('Report generated');
    } catch { toast.error('Failed to generate report'); }
    finally { setGeneratingId(null); }
  };

  const handleExportPDF = (withComp = false) => {
    if (!generatedReport) return;
    const r = generatedReport;
    const tpl = r.template;
    const showComp = withComp && r.show_compensation;

    const doc = new jsPDF('landscape');
    doc.setFontSize(16);
    doc.setTextColor(26, 54, 93);
    doc.text(tpl.name + (showComp ? '' : (r.show_compensation ? ' (No Compensation)' : '')), 14, 15);

    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`${r.date_range.start} to ${r.date_range.end} | Generated: ${new Date().toLocaleDateString()}`, 14, 22);

    doc.setFillColor(240, 242, 241);
    doc.roundedRect(14, 26, 120, 10, 2, 2, 'F');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text(`Grand Total: ${r.grand_total.weighted} ${r.unit_label}${showComp ? ` | $${r.grand_total.compensation.toLocaleString()}` : ''}`, 20, 33);

    // Build headers
    const headers = ['Name', 'Weighted Total'];
    if (showComp) headers.push('Compensation');

    const tableData = [];
    // Grand total row
    const grandRow = [`GRAND TOTAL (${r.grand_total.weighted})`, r.grand_total.weighted];
    if (showComp) grandRow.push(`$${r.grand_total.compensation.toLocaleString()}`);
    tableData.push({ type: 'grand', row: grandRow });

    r.state_order.forEach(state => {
      const group = r.state_groups[state];
      // State header
      const hRow = [state, ''];
      if (showComp) hRow.push('');
      tableData.push({ type: 'header', state, row: hRow });

      group.rows.forEach(row => {
        const dRow = [row.name, row.weighted_total];
        if (showComp) dRow.push(`$${(row.compensation || 0).toFixed(2)}`);
        tableData.push({ type: 'data', row: dRow });
      });

      // Subtotal
      const sRow = [`${state} TOTAL (${group.subtotal_weighted})`, group.subtotal_weighted];
      if (showComp) sRow.push(`$${group.subtotal_compensation.toLocaleString()}`);
      tableData.push({ type: 'subtotal', state, row: sRow });
    });

    autoTable(doc, {
      head: [headers],
      body: tableData.map(i => i.row),
      startY: 40,
      styles: { fontSize: 8, cellPadding: 3, halign: 'center' },
      headStyles: { fillColor: [26, 54, 93], fontStyle: 'bold', textColor: [255, 255, 255] },
      columnStyles: { 0: { cellWidth: 60, halign: 'left' } },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        const item = tableData[data.row.index];
        if (!item) return;
        if (item.type === 'grand') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [26, 54, 93];
          data.cell.styles.textColor = [255, 255, 255];
        } else if (item.type === 'header') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = item.state === 'TX' ? [219, 234, 254] : item.state === 'LA' ? [220, 252, 231] : [255, 237, 213];
        } else if (item.type === 'subtotal') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = item.state === 'TX' ? [191, 219, 254] : item.state === 'LA' ? [187, 247, 208] : [254, 215, 170];
        }
      }
    });

    const suffix = showComp ? '_with_comp' : '';
    doc.save(`${tpl.name.replace(/\s+/g, '_').toLowerCase()}${suffix}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exported!');
  };

  const addColumn = () => {
    setForm(prev => ({
      ...prev,
      columns: [...prev.columns, { field: 'total', label: 'Total', visible: true }]
    }));
  };

  const updateColumn = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      columns: prev.columns.map((c, i) => i === idx ? { ...c, [field]: value } : c)
    }));
  };

  const removeColumn = (idx) => {
    setForm(prev => ({ ...prev, columns: prev.columns.filter((_, i) => i !== idx) }));
  };

  if (loading) {
    return <div className="p-6 flex justify-center"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="report-builder-title">
            <Layers className="text-primary" size={24} />
            Report Builder
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage custom report templates. Reports tied to compensation auto-generate dual versions.
          </p>
        </div>
        <div className="flex gap-2">
          {templates.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults} data-testid="seed-report-defaults-btn">
              Load Defaults
            </Button>
          )}
          <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="new-template-btn">
            <Plus size={14} className="mr-2" /> New Template
          </Button>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <Card className="p-6 border-primary/30" data-testid="template-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{editingId ? 'Edit Template' : 'New Report Template'}</h2>
            <Button variant="ghost" size="sm" onClick={resetForm}><X size={16} /></Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Template Name *</Label>
              <Input data-testid="template-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Therapist Weekly" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label>Group By</Label>
              <Select value={form.group_by} onValueChange={v => setForm({ ...form, group_by: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="therapist">Therapist</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sub-Group By</Label>
              <Select value={form.sub_group_by} onValueChange={v => setForm({ ...form, sub_group_by: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="state">State</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date Range</Label>
              <Select value={form.date_range} onValueChange={v => setForm({ ...form, date_range: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_week">Last Week</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="last_6_weeks">Last 6 Weeks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Report Type</Label>
              <Select value={form.report_type} onValueChange={v => setForm({ ...form, report_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly Summary</SelectItem>
                  <SelectItem value="summary">Period Summary</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap items-center gap-6 mb-4 p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Switch checked={form.show_compensation} onCheckedChange={v => setForm({ ...form, show_compensation: v })} />
              <Label className="flex items-center gap-1 cursor-pointer"><DollarSign size={14} className="text-green-600" /> Show Compensation</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.show_subtotals} onCheckedChange={v => setForm({ ...form, show_subtotals: v })} />
              <Label className="cursor-pointer">Show Subtotals</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.show_grand_total} onCheckedChange={v => setForm({ ...form, show_grand_total: v })} />
              <Label className="cursor-pointer">Show Grand Total</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.include_excluded_tasks} onCheckedChange={v => setForm({ ...form, include_excluded_tasks: v })} />
              <Label className="cursor-pointer">Include Excluded Tasks</Label>
            </div>
          </div>

          {/* Columns */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-semibold">Columns</Label>
              <Button variant="outline" size="sm" onClick={addColumn}><Plus size={14} className="mr-1" /> Add Column</Button>
            </div>
            <div className="space-y-2">
              {form.columns.map((col, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center p-2 bg-muted/20 rounded">
                  <div className="col-span-4">
                    <Select value={col.field} onValueChange={v => {
                      const opt = COLUMN_OPTIONS.find(o => o.field === v);
                      updateColumn(idx, 'field', v);
                      if (opt) updateColumn(idx, 'label', opt.label);
                    }}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {COLUMN_OPTIONS.map(o => <SelectItem key={o.field} value={o.field}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    <Input className="h-8" value={col.label} onChange={e => updateColumn(idx, 'label', e.target.value)} placeholder="Column label" />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    <Switch checked={col.visible} onCheckedChange={v => updateColumn(idx, 'visible', v)} />
                    <span className="text-xs">{col.visible ? <Eye size={12} /> : <EyeOff size={12} />}</span>
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => removeColumn(idx)} className="h-8 w-8 p-0 text-destructive"><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-template-btn"><Save size={14} className="mr-2" /> {editingId ? 'Update' : 'Create'}</Button>
          </div>
        </Card>
      )}

      {/* Templates List */}
      {templates.length === 0 && !showForm ? (
        <Card className="p-12 text-center" data-testid="report-builder-empty">
          <Layers size={48} className="mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-semibold mb-1">No Report Templates Yet</h3>
          <p className="text-muted-foreground mb-4">Create custom report templates or load defaults.</p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={handleSeedDefaults}>Load Defaults</Button>
            <Button onClick={() => setShowForm(true)}><Plus size={14} className="mr-2" /> Create Custom</Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="templates-list">
          {templates.map(t => (
            <Card key={t.id} className="overflow-hidden" data-testid={`template-${t.id}`}>
              <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10 text-primary">
                    <FileText size={20} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{t.name}</h3>
                      {t.show_compensation && (
                        <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <DollarSign size={10} /> Dual Version
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {t.group_by === 'therapist' ? 'By Therapist' : 'By Agency'}
                      {t.sub_group_by === 'state' && ' | Grouped by State'}
                      {` | ${t.date_range?.replace(/_/g, ' ')}`}
                      {` | ${t.columns?.length || 0} columns`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="default" size="sm" disabled={generatingId === t.id} onClick={(e) => { e.stopPropagation(); handleGenerate(t.id); }} data-testid={`generate-${t.id}`}>
                    <Play size={14} className="mr-1" /> {generatingId === t.id ? 'Generating...' : 'Generate'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(t); }}><Edit2 size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(t.id); }} className="text-destructive"><Trash2 size={14} /></Button>
                  {expandedId === t.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {/* Generated Report Preview */}
              {expandedId === t.id && generatedReport && generatedReport.template?.id === t.id && (
                <div className="border-t px-4 pb-4">
                  <div className="flex items-center justify-between py-3">
                    <p className="text-sm text-muted-foreground">
                      {generatedReport.date_range.start} to {generatedReport.date_range.end} | {generatedReport.row_count} rows | Grand Total: {generatedReport.grand_total.weighted} {generatedReport.unit_label}
                      {generatedReport.show_compensation && ` | $${generatedReport.grand_total.compensation.toLocaleString()}`}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleExportPDF(false)} data-testid={`export-pdf-${t.id}`}>
                        <Download size={14} className="mr-1" /> PDF
                      </Button>
                      {generatedReport.show_compensation && (
                        <Button size="sm" variant="outline" onClick={() => handleExportPDF(true)} data-testid={`export-pdf-comp-${t.id}`} className="border-green-300 text-green-700 hover:bg-green-50">
                          <DollarSign size={14} className="mr-1" /> PDF with Comp
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Data Table Preview */}
                  <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-center p-2">Weighted Total</th>
                          {generatedReport.show_compensation && <th className="text-center p-2">Compensation</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Grand total */}
                        <tr className="bg-primary text-white font-bold">
                          <td className="p-2">GRAND TOTAL ({generatedReport.grand_total.weighted})</td>
                          <td className="text-center p-2">{generatedReport.grand_total.weighted}</td>
                          {generatedReport.show_compensation && <td className="text-center p-2">${generatedReport.grand_total.compensation.toLocaleString()}</td>}
                        </tr>
                        {generatedReport.state_order.map((state, si) => {
                          const group = generatedReport.state_groups[state];
                          const colors = state === 'TX' ? 'bg-blue-100' : state === 'LA' ? 'bg-green-100' : 'bg-orange-100';
                          const subColors = state === 'TX' ? 'bg-blue-200' : state === 'LA' ? 'bg-green-200' : 'bg-orange-200';
                          return (
                            <React.Fragment key={si}>
                              <tr className={colors}>
                                <td colSpan={generatedReport.show_compensation ? 3 : 2} className="p-2 font-bold">{state}</td>
                              </tr>
                              {group.rows.map((row, ri) => (
                                <tr key={ri} className="border-b hover:bg-muted/20">
                                  <td className="p-2">{row.name}</td>
                                  <td className="text-center p-2">{row.weighted_total}</td>
                                  {generatedReport.show_compensation && <td className="text-center p-2">${(row.compensation || 0).toFixed(2)}</td>}
                                </tr>
                              ))}
                              <tr className={`${subColors} font-bold`}>
                                <td className="p-2">{state} TOTAL ({group.subtotal_weighted})</td>
                                <td className="text-center p-2">{group.subtotal_weighted}</td>
                                {generatedReport.show_compensation && <td className="text-center p-2">${group.subtotal_compensation.toLocaleString()}</td>}
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
